import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowUpRight } from "lucide-react"
import { z } from "zod"
import { RUN_STATUS_LABELS, RunStatus } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AutoHeading, Choice, DotBadge, Metrics, Note } from "@/components/automations/primitives"
import { AutomationsNav } from "@/components/automations/section-nav"
import { formatDateTime, initials } from "@/lib/format"
import { runsQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/runs/")({
  validateSearch: z.object({ automationId: z.string().optional() }),
  component: RunHistory,
})

function duration(seconds: number | null) {
  if (seconds === null) return "—"
  if (seconds < 60) return `${Math.round(seconds)} sec`
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  return `${Math.round(seconds / 3600)} h`
}

/** Screen 22. */
function RunHistory() {
  const { workspace } = useWorkspaceContext()
  const { automationId } = Route.useSearch()
  const [status, setStatus] = useState("all")
  const [query, setQuery] = useState("")
  const [tab, setTab] = useState("runs")
  const page = useQuery({ ...runsQuery(workspace.slug, { automationId, status: status === "all" ? undefined : status, q: query || undefined }), placeholderData: (previous) => previous })
  const params = { workspace: workspace.slug }
  const data = page.data
  const rows = data?.runs ?? []
  return (
    <div className="page-wrap auto-page">
      <AutoHeading title="Logs" description={automationId && rows[0] ? `${rows[0].automationName} · last 7 days` : "Every run, its outcome and the events that were skipped, with the last seven days summarised on top."}>
        {automationId && (
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/automations/$automationId" params={{ ...params, automationId }}>
              Open automation
              <ArrowUpRight />
            </Link>
          </Button>
        )}
      </AutoHeading>
      <AutomationsNav workspace={workspace.slug} />
      <Metrics
        items={[
          { label: "Runs started", value: String(data?.metrics.started ?? 0), detail: "Last 7 days" },
          { label: "Completed", value: String(data?.metrics.completed ?? 0), detail: data?.metrics.started ? `${Math.round(((data.metrics.completed ?? 0) / data.metrics.started) * 100)}% of started runs` : "No runs yet" },
          { label: "Handed over", value: String(data?.metrics.handedOver ?? 0), detail: data?.metrics.started ? `${Math.round(((data.metrics.handedOver ?? 0) / data.metrics.started) * 100)}% of started runs` : "No runs yet" },
          { label: "Needs review", value: String(data?.metrics.needsReview ?? 0), detail: "Delivery issues or blocked runs" },
        ]}
      />
      <Tabs value={tab} onValueChange={setTab} className="mt-7">
        <div className="auto-toolbar mb-4">
          <TabsList>
            <TabsTrigger value="runs">Runs</TabsTrigger>
            <TabsTrigger value="skipped">
              Skipped events <Badge variant="secondary">{(data?.skipped ?? []).reduce((a, s) => a + s.count, 0)}</Badge>
            </TabsTrigger>
          </TabsList>
          <Choice label="Run outcome" value={status} onChange={setStatus} values={[{ value: "all", label: "All outcomes" }, ...RunStatus.options.filter((s) => s !== "advancing").map((s) => ({ value: s, label: RUN_STATUS_LABELS[s] }))]} />
        </div>
        <TabsContent value="runs">
          <Input className="mb-4 sm:w-72" placeholder="Search contacts…" aria-label="Search run contacts" value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Contact</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Last step</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="pr-5 text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="py-4 pl-5">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>{initials(run.contactName)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <Link className="font-medium hover:underline" to="/w/$workspace/automations/runs/$runId" params={{ ...params, runId: run.id }}>
                            {run.contactName}
                          </Link>
                          <p className="mt-1 text-xs text-muted-foreground">{run.handle ?? run.automationName}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDateTime(run.startedAt)}</TableCell>
                    <TableCell>
                      <DotBadge warn={run.needsReview}>{run.needsReview && run.status !== "failed" && run.status !== "blocked" ? "Unknown delivery" : RUN_STATUS_LABELS[run.status]}</DotBadge>
                    </TableCell>
                    <TableCell>{run.lastStepTitle ?? "—"}</TableCell>
                    <TableCell>v{run.versionNumber}</TableCell>
                    <TableCell className="pr-5 text-right tabular-nums">{duration(run.durationSeconds)}</TableCell>
                  </TableRow>
                ))}
                {page.isSuccess && !rows.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-14 text-center text-muted-foreground">
                      No runs match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Showing {rows.length} runs.</p>
        </TabsContent>
        <TabsContent value="skipped">
          <Card>
            <CardHeader>
              <CardTitle>Events that did not start a run</CardTitle>
              <CardDescription>These are excluded from the started-run denominator.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reason</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>What happened</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.skipped ?? []).map((row) => (
                    <TableRow key={row.reason}>
                      <TableCell className="py-4">{row.label}</TableCell>
                      <TableCell className="py-4 tabular-nums">{row.count}</TableCell>
                      <TableCell className="py-4">{row.detail}</TableCell>
                    </TableRow>
                  ))}
                  {(data?.skipped ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                        No skipped events in the last 7 days.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <Note>Unknown delivery requires reconciliation before retry. A timeout does not prove that the customer received nothing.</Note>
    </div>
  )
}
